import type { DefaultOptionType } from '@/components/combobox/combobox';
import PriceUnitSelection from '@/components/combobox/price-unit-selection';
import NumericTextField from '@/components/form/numeric-text-field';
import { Button, HelpText, Label, Modal } from '@lego/connect-components-react';
import { type RefObject, useState } from 'react';

type DownloadModalProps = {
  isOpen: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  onSubmit: (
    year: number,
    priceUnitCategoryId: string,
    priceUnitIds: string[],
  ) => void;
  onClose: () => void;
};

const DownloadModal = ({
  isOpen,
  triggerRef,
  onClose,
  onSubmit,
}: DownloadModalProps) => {
  const [year, setYear] = useState<number | null>(new Date().getFullYear());
  const [yearError, setYearError] = useState<string | undefined>();
  const [selectedPriceUnits, setSelectedPriceUnits] = useState<
    DefaultOptionType[]
  >([]);
  const [selectedPriceUnitCategory, setSelectedPriceUnitCategory] =
    useState('');
  const [hasError, setHasError] = useState(false);

  const handleClose = () => {
    setYear(null);
    setYearError(undefined);
    setSelectedPriceUnits([]);
    setSelectedPriceUnitCategory('');
    setHasError(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      triggerRef={triggerRef}
      onClose={handleClose}
      size={'small'}
    >
      <Modal.Title>Excel download</Modal.Title>
      <Modal.Content>
        <div>
          <Label>Year</Label>
          <NumericTextField
            placeholder={new Date().getFullYear().toString()}
            value={year !== null ? year.toString() : ''}
            onChange={(e) => {
              const raw = e.target.value;
              setYear(raw === '' ? null : Number(raw));
            }}
            validationState={yearError ? 'invalid' : undefined}
            integerOnly
            onBlur={(e) => {
              const val = e.target.value;
              setYearError(
                val !== '' && (Number(val) < 1000 || Number(val) > 9999)
                  ? 'Year must be a 4-digit number'
                  : undefined,
              );
            }}
          />
          {yearError && <HelpText variant="negative">{yearError}</HelpText>}
        </div>
        <PriceUnitSelection
          selectedCategory={selectedPriceUnitCategory}
          onCategoryChange={setSelectedPriceUnitCategory}
          selectedPriceUnits={selectedPriceUnits}
          onPriceUnitsChange={setSelectedPriceUnits}
          setHasError={setHasError}
        />
      </Modal.Content>
      <Modal.Footer>
        <Button onClick={handleClose} appearance="ghost" aria-label={'Cancel'}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (year === null) return;
            onSubmit(
              year,
              selectedPriceUnitCategory,
              selectedPriceUnits.map((priceUnit) => priceUnit.id),
            );
          }}
          aria-label={'Submit'}
          disabled={
            year === null ||
            !!yearError ||
            hasError ||
            (!selectedPriceUnitCategory && selectedPriceUnits.length === 0)
          }
        >
          Download
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
export default DownloadModal;
